import React from "react";
import { Link } from "react-router-dom";
import { Logo } from "../components/Logo";
import { Flame, Hand, Award, Heart } from "lucide-react";
import { JOBBER_BOOKING_URL } from "../lib/brand";

const ABOUT_IMG = "https://customer-assets.emergentagent.com/job_fuego-detailing/artifacts/4wn1k6da_IMG_0565.jpg";

const values = [
  { icon: Hand, title: "Hands-On Care", desc: "Every panel touched, every fiber considered. Hand-finished, never rushed." },
  { icon: Award, title: "Quality First", desc: "Pro-grade products and proven techniques only. Results that hold up." },
  { icon: Heart, title: "Customer Obsessed", desc: "Your time, your ride, your trust. Earned on every appointment." },
];

const AboutPage = () => {
  return (
    <div data-testid="about-page">
      <section className="relative pt-24 pb-16 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="font-display uppercase tracking-[0.25em] text-xs text-[#D90429] mb-4">About</div>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-black uppercase tracking-tighter leading-[0.95]">
            The story behind <span className="text-[#D90429]">Fuego.</span>
          </h1>
        </div>
      </section>

      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-16 items-start">
          <div className="relative">
            <div className="absolute -inset-3 border border-[#D90429]/30 pointer-events-none" />
            <div className="absolute -inset-3 bg-gradient-to-br from-[#D90429]/15 via-transparent to-transparent pointer-events-none" />
            <img
              src={ABOUT_IMG}
              alt="Owner of Fuego Precision Detailing"
              className="relative w-full aspect-[4/5] object-cover object-[center_20%]"
              loading="lazy"
            />
            <div className="absolute bottom-0 left-0 right-0 h-1/3 bg-gradient-to-t from-black/70 to-transparent pointer-events-none" />
            <div className="absolute bottom-6 left-6 right-6 z-10">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-black/70 border border-[#D90429]/50 backdrop-blur-sm">
                <span className="w-2 h-2 bg-[#D90429] rounded-full flame-pulse" />
                <span className="font-display uppercase tracking-[0.25em] text-[10px] text-white">Owner · Operator</span>
              </div>
            </div>
          </div>
          <div>
            <div className="mb-8"><Logo size="lg" /></div>
            <h2 className="font-display text-3xl sm:text-4xl font-bold uppercase tracking-tight">
              Hi, I'm the owner.
            </h2>
            <div className="mt-6 space-y-5 text-[#D4D4D4] leading-relaxed">
              <p>
                I started Fuego Precision Detailing because cars deserve more than a quick wash. They deserve craftsmanship. From the first time I saw paint come back to life under a polisher, I was hooked — and I've been chasing that perfect finish ever since.
              </p>
              <p>
                Whether it's a daily driver, a family SUV, or a weekend track build, my job is the same: treat your vehicle like my own and leave it looking better than the day you got the keys. No cut corners. No filler talk. Just clean, careful, honest work.
              </p>
              <p>
                Booking is simple, results are obvious, and every detail matters — because that's the only way I know how to work.
              </p>
            </div>
            <a href={JOBBER_BOOKING_URL} data-testid="about-book-now" className="mt-10 inline-flex items-center gap-2 bg-[#D90429] hover:bg-[#EF233C] text-white font-display font-bold tracking-wider uppercase px-8 py-4 transition-colors">
              <Flame size={16}/> Book your detail
            </a>
          </div>
        </div>
      </section>

      <section className="py-24 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="font-display uppercase tracking-[0.25em] text-xs text-[#D90429] mb-3">What I stand for</div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold uppercase tracking-tight mb-12">The Fuego standard.</h2>
          <div className="grid md:grid-cols-3 gap-px bg-[#262626]">
            {values.map((v) => (
              <div key={v.title} className="bg-[#050505] p-10 hover:bg-[#0a0a0a] transition-colors" data-testid={`about-value-${v.title.toLowerCase().replace(/\s+/g, "-")}`}>
                <v.icon size={28} className="text-[#D90429] mb-5" />
                <h3 className="font-display text-xl font-bold uppercase tracking-tight">{v.title}</h3>
                <p className="mt-3 text-sm text-[#A3A3A3] leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
